<script lang="ts" setup>
import type { ContentFile } from '~/models/Content/ContentFile'
import { useI18n } from 'vue-i18n'
import { computed, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useQuery } from '@tanstack/vue-query'
import { faDownload } from '@fortawesome/duotone-regular-svg-icons'
import { setTitle } from '~/utils/dom'
import useLicenseControlStore from '~/stores/licenseControl'
import useAuthStore from '~/stores/auth'
import { Subtree } from '~/models/Content/Subtree'
import { ContentType } from '~/models/Content/ContentType'
import { useUrlBuilder } from '~/composables/useUrlBuilder'
import useFileInfo from '~/composables/useFileInfo'
import useContentApi from '~/api/contentApi'
import TopBar from '~/components/utils/TopBar.vue'
import Button from '~/components/ui/Button.vue'

const props = defineProps<{ locationId: number }>()

const { t } = useI18n()
const { findContents } = useContentApi()
const { buildResourceUrl } = useUrlBuilder()
const { checkItemLicense } = useLicenseControlStore()
const { readableMimeType, readableFileSize } = useFileInfo()
const { isAuthenticated } = storeToRefs(useAuthStore())

const { data } = useQuery({
  queryKey: computed(() => ['file-page', props.locationId]),
  queryFn: () => findContents<ContentFile>({
    subtreeCriterion: [Subtree.Content, Subtree.Media],
    contentTypeCriterion: [ContentType.File],
    locationIdCriterion: [Number(props.locationId)],
  }).then(([content]) => content),
  staleTime: Infinity,
  retry: 1,
})

const mimeType = computed(() => data?.value?.file?.mimeType
  ? readableMimeType(data.value.file.mimeType || '')
  : '')

const fileSize = computed(() => data?.value?.file?.fileSize
  ? readableFileSize(data.value.file.fileSize || 0)
  : '')

const fileInfo = computed(() => [mimeType.value, fileSize.value].filter(Boolean).join(' '))
const fileUrl = computed(() => data.value && isAuthenticated.value ? buildResourceUrl(data.value) : null)

watch(data, async (value) => {
  if (!value)
    return
  setTitle(value.title)
  await checkItemLicense(value)
}, { immediate: true })
</script>

<template>
  <div class="flex h-screen">
    <TopBar
      v-if="data"
      :item="data"
    />
    <div class="m-auto">
      <Button
        v-if="data && fileUrl"
        :href="fileUrl"
        size="lg"
      >
        <font-awesome-icon :icon="faDownload" />
        {{ t('downloadIn', { in: `"${data.title}"` }) }}
      </Button>
      <p
        v-if="fileInfo"
        class="text-gray-40 mt-2 text-center text-sm"
        v-text="fileInfo"
      />
    </div>
  </div>
</template>
